import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'admin_field_mode_v1';

type FieldModeContextValue = {
  fieldMode: boolean;
  setFieldMode: (enabled: boolean) => void;
  toggleFieldMode: () => void;
};

const FieldModeContext = createContext<FieldModeContextValue | undefined>(undefined);

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function FieldModeProvider({ children }: { children: React.ReactNode }) {
  const [fieldMode, setFieldModeState] = useState(() => readStored());

  const setFieldMode = useCallback((enabled: boolean) => {
    setFieldModeState(enabled);
    try {
      if (enabled) window.localStorage.setItem(STORAGE_KEY, '1');
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleFieldMode = useCallback(() => {
    setFieldMode(!readStored());
  }, [setFieldMode]);

  const value = useMemo(
    () => ({ fieldMode, setFieldMode, toggleFieldMode }),
    [fieldMode, setFieldMode, toggleFieldMode]
  );

  return <FieldModeContext.Provider value={value}>{children}</FieldModeContext.Provider>;
}

export function useFieldMode(): FieldModeContextValue {
  const ctx = useContext(FieldModeContext);
  if (!ctx) {
    throw new Error('useFieldMode must be used within FieldModeProvider');
  }
  return ctx;
}

export function isFieldModeEnabled(): boolean {
  return readStored();
}

/** React state + localStorage — avoids a race where guards run before context hydrates. */
export function getEffectiveFieldMode(fieldMode: boolean): boolean {
  return fieldMode || isFieldModeEnabled();
}
